import { Dropdown, Modal, Col, Row, Space, Typography, message } from "antd";
import { useState } from "react";
import { Guid } from "guid-typescript";
import { SearchQuery } from "../code/search-query/search-query";
import Loader from "../controls/Loader";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getCounsellingRequestsExport } from "../repository/data.repository";
import CounsellingRequestsView from "../components/counselling-requets/CounsellingRequestsView";
import { CounsellingRequest } from "../repository/data.default";
import CounsellingRequestFormDialog from "../components/counselling-requets/CounsellingRequestFormDialog";
import CounsellingManageDialog from "../components/counselling-requets/CounsellingManageDialog";
import { useCalendarEventPreview } from "../components/useCalendarEventPreview";
import { getProblemMessage } from "../utils/http";

function CounsellingRequestsPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [requestInputMode, setRequestInputMode] = useState<
    "add" | "delete" | "manage"
  >("add");
  const [requestInputOpen, setRequestInputOpen] = useState(false);
  const [requestInputRecord, setRequestInputRecord] = useState<
    CounsellingRequest | undefined
  >(undefined);
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  // Deep-link: the calendar event behind a scheduled request (opened read-only from the Manage dialog).
  const { openPreview, previewDialog } = useCalendarEventPreview();
  const permissions = useSelector(authorizeSelectors.permissions);

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
    setTriggerView(Guid.create().toString());
  }

  function onRequestAction(
    mode: "add" | "delete" | "manage",
    record?: CounsellingRequest,
  ): void {
    setRequestInputRecord(record);
    setRequestInputMode(mode);
    setRequestInputOpen(true);
  }

  function onRequestInputFinish(): void {
    setRequestInputOpen(false);
    setTriggerView(Guid.create().toString());
  }

  function onRequestInputClose(): void {
    setRequestInputOpen(false);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading counselling requests export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getCounsellingRequestsExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Counselling Requests
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("CounsellingRequests", "create")}

            className={`${!permissions.can("CounsellingRequests", "create") ? "disabled" : ""}`}
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("CounsellingRequests", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={() => onRequestAction("add")}
          >
            Add Request
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <CounsellingRequestsView
          trigger={triggerView}
          onAction={(key: string, record: any) =>
            onRequestAction(key as any, record)
          }
          onSearch={onSearch}
        />
      </Col>
      <CounsellingRequestFormDialog
        mode={requestInputMode === "manage" ? "add" : requestInputMode}
        open={requestInputOpen && requestInputMode !== "manage"}
        record={requestInputRecord}
        onFinish={onRequestInputFinish}
        onCancel={onRequestInputClose}
      />
      <CounsellingManageDialog
        open={requestInputOpen && requestInputMode === "manage"}
        record={requestInputRecord}
        onFinish={onRequestInputFinish}
        onCancel={onRequestInputClose}
        onViewEvent={openPreview}
      />
      {previewDialog}
      {contextHolder}
    </Col>
  );
}

export default CounsellingRequestsPage;
